/**
 * Team Insights redesign — the "View follow-ups" / "Total scheduled" lists on the team card (pure, import-free).
 *
 * The classic pages open a popover from a button ("31 follow-up(s)", "13 appointment(s)  $2,066.00"). The redesigned card opens the same
 * lists in place. This file turns the SAME arrays the popovers loop over into the card's `chips` prop, and does no counting of its own:
 * a chip's count is the array's length and its value is the same sum the classic line shows. The page hands in how to format a name, a
 * date and money (`fmt`), because those depend on the app (PHI masking, the site's date format and currency).
 * cypress/node-tests/team-insights-outcome-model.test.js executes this exact file.
 */

/**
 * The list of follow-ups made. Rows are [patient, date, team member], like the classic popover.
 * fmt: { name(patient), date(value) }
 */
export function buildCallsChip(items, fmt) {
  const list = Array.isArray(items) ? items : [];
  return {
    id: 'calls',
    kind: 'calls',
    label: 'View follow-ups',
    count: list.length,
    title: 'Follow-up(s) made',
    columns: ['Patient', 'Followed-up on', 'Team member'],
    rows: list.map(a => [fmt.name(a.Patient), fmt.date(a.CreatedAt), a.Employee || '']),
  };
}

/**
 * A list with a money value per row (appointments scheduled, claims received). Rows are [patient, value].
 * cfg: { id, label ('Total scheduled'), noun ('appointment(s)'), title, valueKey, valueColumn }
 * fmt: { name(patient), money(value) for a row, moneyTotal(value) for the chip's total }
 */
export function buildValueChip(items, cfg, fmt) {
  const list = Array.isArray(items) ? items : [];
  const total = list.reduce((a, b) => a + (b[cfg.valueKey] || 0), 0);
  return {
    id: cfg.id,
    kind: 'value',
    label: cfg.label,
    count: list.length,
    noun: cfg.noun,
    value: fmt.moneyTotal(total),
    title: cfg.title,
    columns: ['Patient', cfg.valueColumn],
    rows: list.map(a => [fmt.name(a.Patient), fmt.money(a[cfg.valueKey])]),
  };
}

/**
 * The four numbers shown on a team card and on each person's card: follow-ups, scheduled (or received), their value, and the
 * conversion rate. Nothing is recounted: `total` is the classic follow-up total, `items` the same array the classic "Total scheduled"
 * line counts, and the value the same sum it shows.
 *
 * The rate is the app's own "Conversion Rate" (Team Insights has computed it for every team member for a long time):
 * Math.ceil(scheduled ÷ follow-ups × 100). A person's card is handed the number the classic code already stored on them
 * (`rateOverride`); the team card has no classic figure, so it uses the same formula. No follow-ups means 0%, not NaN.
 *
 * cfg: the value-chip cfg plus { short: 'Scheduled', valueLabel: 'Scheduled value' }; null when the section has no second measure
 * (Patient Balances), which leaves just the follow-ups number.
 * Each stat that opens a list names it in `opens` (the id of the chip with that list).
 */
export function buildStats(total, items, cfg, fmt, rateOverride) {
  const calls = { id: 'calls', label: 'Follow-ups', value: String(total), sub: 'in this period', opens: 'calls' };
  if (!cfg) return [calls];
  const list = Array.isArray(items) ? items : [];
  const amount = list.reduce((a, b) => a + (b[cfg.valueKey] || 0), 0);
  const computed = total > 0 ? Math.ceil((list.length / total) * 100) : 0;
  const rate = typeof rateOverride === 'number' && Number.isFinite(rateOverride) ? rateOverride : computed;
  return [
    calls,
    { id: cfg.id, label: cfg.short, value: String(list.length), sub: cfg.noun, opens: cfg.id },
    { id: 'value', label: cfg.valueLabel, value: fmt.moneyTotal(amount), sub: `across ${list.length} ${cfg.noun}` },
    { id: 'rate', label: 'Conversion rate', value: `${rate}%`, sub: `${cfg.short.toLowerCase()} ÷ follow-ups` },
  ];
}

/**
 * The line under the team tiles that explains why the people on the cards add up to less than the team ('' when they add up).
 *
 * The scheduled / received items are credited to whoever created the task, and a person gets a card only if they logged a follow-up in
 * the period (the classic rule, unchanged), so an item credited to someone without a card is in the team total but on no person's
 * card. This only SAYS so; it counts nothing the page does not already count.
 * cfg: the value-chip cfg plus { unit: 'appointment' | 'claim' }.  nameOf(employeeID) → the person's name ('' when unknown).
 */
export function buildUnassignedNote(items, members, cfg, nameOf) {
  if (!cfg) return '';
  const list = Array.isArray(items) ? items : [];
  const shown = {};
  (Array.isArray(members) ? members : []).forEach((m) => { shown[m.empID] = true; });
  const missing = list.filter(i => !shown[i.CreatedByEmpID]);
  if (missing.length === 0) return '';
  const names = [];
  missing.forEach((i) => {
    const n = (typeof nameOf === 'function' && nameOf(i.CreatedByEmpID)) || 'someone no longer on the team list';
    if (names.indexOf(n) === -1) names.push(n);
  });
  const who = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  const n = missing.length;
  return `${n} ${cfg.short.toLowerCase()} ${cfg.unit}${n === 1 ? ' is' : 's are'} credited to ${who}, who ${names.length === 1 ? 'has' : 'have'} no follow-ups in this period, so ${n === 1 ? 'it is' : 'they are'} in the team total but not on a person's card.`;
}

/**
 * The same members, each with its own `stats`: that person's follow-ups (their classic total), the items they created (the classic
 * per-person filter: CreatedByEmpID === their employee ID) and the conversion rate the classic code stored on them.
 */
export function buildMemberStats(members, items, cfg, fmt) {
  const list = Array.isArray(items) ? items : [];
  return (Array.isArray(members) ? members : []).map(m => Object.assign({}, m, {
    stats: buildStats(m.totalCalls, list.filter(i => i.CreatedByEmpID === m.empID), cfg, fmt, m.conversionRate),
  }));
}
